import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatRs = (n) => (n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);

const formatTimestamp = (iso) => {
  if (!iso) return "Never";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
};

export default function LivePricesTab() {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchPrices = useCallback(async () => {
    try {
      // Auth flows via the httpOnly access_token cookie (withCredentials is set globally).
      const res = await axios.get(`${API}/prices`);
      setPrices(res.data);
    } catch (err) {
      console.error("Failed to load prices:", err);
      toast.error("Could not load live prices");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/prices/refresh`, {});
      setPrices(res.data.prices);
      const failed = res.data.summary?.failed?.length || 0;
      const updated = res.data.summary?.updated?.length || 0;
      if (failed === 0) {
        toast.success(`Refreshed ${updated} product price${updated !== 1 ? "s" : ""} from portl.co`);
      } else {
        toast.warning(`Refreshed ${updated} of ${updated + failed} products. ${failed} fetch issue${failed !== 1 ? "s" : ""}.`);
      }
    } catch (err) {
      console.error("Failed to refresh prices:", err);
      toast.error("Refresh failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return <div className="text-[#707070] text-sm" data-testid="live-prices-loading">Loading live prices…</div>;
  }

  const newestFetch = prices.reduce((acc, p) => {
    if (!p.last_fetched_at) return acc;
    const t = new Date(p.last_fetched_at).getTime();
    return t > acc ? t : acc;
  }, 0);

  return (
    <div data-testid="live-prices-tab" className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-white">Live Prices from portl.co</h2>
          <p className="text-xs text-[#888] mt-1 max-w-2xl leading-relaxed">
            Arya quotes these exact numbers in every chat. Scraped directly from the product pages and auto-refreshed once a day. Hit refresh to re-fetch immediately.
          </p>
          <p className="text-[10px] text-[#666] mt-1">
            Last refreshed: <span className="text-[#E85D24]" data-testid="prices-last-refreshed">{formatTimestamp(newestFetch ? new Date(newestFetch).toISOString() : null)}</span>
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-prices-btn"
          className="shrink-0 px-4 py-2 text-xs font-medium bg-[#E85D24] hover:bg-[#E85D24]/85 disabled:bg-[#555] disabled:cursor-not-allowed text-white rounded transition-colors"
        >
          {refreshing ? "Refreshing…" : "Refresh Now"}
        </button>
      </div>

      <div className="overflow-x-auto rounded border border-[#1F1F1F]">
        <table className="w-full text-sm">
          <thead className="bg-[#0F0F0F] text-[10px] uppercase tracking-wider text-[#707070]">
            <tr>
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-right px-4 py-3">Current</th>
              <th className="text-right px-4 py-3">MRP</th>
              <th className="text-right px-4 py-3">Discount</th>
              <th className="text-right px-4 py-3">EMI</th>
              <th className="text-center px-4 py-3">Stock</th>
              <th className="text-left px-4 py-3">Last Verified</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1A1A1A]">
            {prices.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-[#666] text-xs">
                  No prices cached yet — hit "Refresh Now" to fetch from portl.co
                </td>
              </tr>
            ) : (
              prices.map((p) => (
                <tr key={p.slug} data-testid={`price-row-${p.slug}`} className="hover:bg-[#0E0E0E] transition-colors">
                  <td className="px-4 py-3">
                    <div className="text-white font-medium">{p.name}</div>
                    <a
                      href={p.source_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] text-[#555] hover:text-[#E85D24] transition-colors"
                    >
                      {p.source_url.replace("https://", "")}
                    </a>
                  </td>
                  <td className="text-right px-4 py-3 text-[#E85D24] font-semibold">{formatRs(p.current_price)}</td>
                  <td className="text-right px-4 py-3 text-[#888]">
                    {p.mrp && p.current_price && p.mrp > p.current_price ? (
                      <span className="line-through">{formatRs(p.mrp)}</span>
                    ) : (
                      formatRs(p.mrp)
                    )}
                  </td>
                  <td className="text-right px-4 py-3 text-[#32D74B]">{p.discount_pct ? `${p.discount_pct}% off` : "—"}</td>
                  <td className="text-right px-4 py-3 text-[#A0A0A0]">{p.emi_per_month ? `${formatRs(p.emi_per_month)}/mo` : "—"}</td>
                  <td className="text-center px-4 py-3">
                    {p.in_stock ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/30">In stock</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/30">Out of stock</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-[#888]">{formatTimestamp(p.last_fetched_at)}</td>
                  <td className="px-4 py-3 text-[11px]">
                    {p.last_fetch_ok ? (
                      <span className="text-[#32D74B]">OK</span>
                    ) : (
                      <span className="text-[#FF3B30]" title={p.last_error || ""}>Stale (last fetch failed)</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-[#666] leading-relaxed border border-[#1A1A1A] rounded p-3 bg-[#0A0A0A]">
        <strong className="text-[#888]">How this works:</strong> The backend scrapes the live product pages once a day and stores the latest price, MRP, discount and stock state. Arya is instructed (with strict guardrails) to quote ONLY these numbers — never to invent or recall pricing from memory. If a fetch fails, the previously cached values are kept and the status column shows <em>Stale</em>.
      </div>
    </div>
  );
}
